/**
 * SEO helper — Ceciluz Arte.
 *
 * En F3 el SEO vive en este módulo + un hook `useSEO()`.
 * En F6 (admin) migrará a leer `site_settings.seo_defaults` y
 * `pages_copy[seo:<slug>]`. F8 considera switch a `react-helmet-async`.
 *
 * Cada ruta importa su SEO al montar:
 *   useSEO(pageSEO.home);   // o pageSEO.terapiaDetail({ slug })
 *
 * Hot-path: una sola escritura por mount.
 */
import { useEffect } from 'react';

import { requireEnv } from './env';

// ---------- Tipos ----------

export interface SEOConfig {
  /** Título completo (incluye sufijo de marca). */
  title: string;
  /** Meta description ≤ 160 caracteres. */
  description: string;
  /** Palabras clave (deprecated pero algunos crawlers aún las leen). */
  keywords?: string[];
  /** URL canónica absoluta. Si falta, no se emite <link rel=canonical>. */
  canonical?: string;
  /** OG image URL absoluta. F1 genera vía edge `og-image`. */
  ogImage?: string;
  /** Tipo Open Graph (default `website`). */
  ogType?: 'website' | 'article' | 'product' | 'event';
}

/**
 * URL canónica del sitio. Alimenta `<link rel="canonical">`, las etiquetas
 * Open Graph y el sitemap.
 *
 * QA 2026-09-23: antes era `import.meta.env.VITE_SITE_URL ?? 'https://
 * ceciluzarte.com'`. Ese fallback convertía un error de configuración en un
 * comportamiento silencioso y equivocado: sin la variable, cada página se
 * anunciaba a Google y a las redes sociales bajo un dominio que no
 * controlamos, y nada lo delataba.
 *
 * `requireEnv` tira con un mensaje explícito. Como Vite resuelve estas
 * variables en build-time, el fallo aparece al arrancar o al compilar —
 * cuando todavía se puede arreglar— y no en producción semanas después.
 */
export const SITE_URL = requireEnv('VITE_SITE_URL');
const BRAND = 'Ceciluz Arte';

/** Sufijo añadido si el título no incluye ya la marca. */
function withBrand(title: string): string {
  return title.includes(BRAND) ? title : `${title} | ${BRAND}`;
}

/** Trunca description a 160 chars con elipsis. */
function trim(desc: string, max = 160): string {
  if (desc.length <= max) return desc;
  return desc.slice(0, max - 1).trimEnd() + '…';
}

// ---------- Defaults ----------

export const defaultSEO: SEOConfig = {
  title: BRAND,
  description:
    'Arte que sana. Energía que transforma. Alma que conecta. Terapias holísticas, piezas de arte únicas, eventos y rituales.',
  keywords: ['ceciluz', 'arte sanador', 'terapias holísticas', 'reiki', 'arteterapia'],
  ogType: 'website',
};

// ---------- Catálogo por ruta ----------

export const pageSEO = {
  home: {
    title: withBrand('Inicio'),
    description: defaultSEO.description,
  },
  therapies: {
    title: withBrand('Terapias holísticas'),
    description:
      'Sesiones de Reiki, arteterapia, consejería espiritual y más. Espacios de sanación y acompañamiento para tu bienestar integral.',
  },
  shop: {
    title: withBrand('Tienda'),
    description:
      'Cuadros, bolsos pintados, cajas intervenidas y productos artesanales. Cada pieza con intención y armonización.',
  },
  customOrder: {
    title: withBrand('Encargo personalizado'),
    description: 'Crea una pieza única hecha a mano con tu intención y propósito.',
  },
  gallery: {
    title: withBrand('Galería'),
    description: 'Explora cuadros, bolsos, cajas, rituales y procesos creativos de Ceciluz Arte.',
  },
  about: {
    title: withBrand('Sobre mí'),
    description: 'Hola, soy Ceci. Mi historia, mis valores y mi proceso creativo.',
  },
  events: {
    title: withBrand('Eventos que elevan tu alma'),
    description: 'Ceremonias, talleres, experiencias y rituales. Encuentra tu próximo evento.',
  },
  contact: {
    title: withBrand('Contacto'),
    description: '¿Dudas o consultas? Escríbeme por WhatsApp, email o el formulario.',
  },
  login: { title: withBrand('Iniciar sesión'), description: 'Accede a tu cuenta Ceciluz Arte.' },
  register: {
    title: withBrand('Crear cuenta'),
    description: 'Crea tu cuenta y reserva sesiones, encargos y eventos.',
  },
  recover: {
    title: withBrand('Recuperar contraseña'),
    description: 'Te enviaremos un enlace de recuperación a tu email.',
  },
  account: {
    title: withBrand('Mi cuenta'),
    description: 'Tus reservas, pedidos y favoritos en un solo lugar.',
  },
  admin: { title: withBrand('Panel admin'), description: 'Operación interna de Ceciluz Arte.' },
  paymentResult: {
    title: withBrand('Resultado del pago'),
    description: 'Verificación del estado de tu pago.',
  },
  notFound: {
    title: withBrand('404 — Página no encontrada'),
    description: 'La página que buscas no existe.',
  },
  serverError: {
    title: withBrand('Error del servidor'),
    description: 'Ocurrió un error inesperado.',
  },
  privacy: {
    title: withBrand('Política de privacidad'),
    description: 'Cómo tratamos tus datos personales.',
  },
  terms: {
    title: withBrand('Términos y condiciones'),
    description: 'Términos de uso del sitio y servicios.',
  },
  cookies: {
    title: withBrand('Política de cookies'),
    description: 'Cómo usamos cookies en el sitio.',
  },
} as const satisfies Record<string, SEOConfig>;

export type PageSEOKey = keyof typeof pageSEO;

/** Builders para rutas con parámetro (slug). F4 los completa con datos reales. */
export const seoBuilders = {
  therapyDetail: (slug: string): SEOConfig => ({
    title: withBrand(slug.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase())),
    description: `Detalle de terapia ${slug.replace(/-/g, ' ')} en Ceciluz Arte.`,
    canonical: `${SITE_URL}/terapias/${slug}`,
  }),
  productDetail: (slug: string): SEOConfig => ({
    title: withBrand(slug.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase())),
    description: `Producto ${slug.replace(/-/g, ' ')} en Ceciluz Arte.`,
    canonical: `${SITE_URL}/tienda/${slug}`,
  }),
  eventDetail: (slug: string): SEOConfig => ({
    title: withBrand(slug.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase())),
    description: `Evento ${slug.replace(/-/g, ' ')} en Ceciluz Arte.`,
    canonical: `${SITE_URL}/eventos/${slug}`,
    ogType: 'event',
  }),
};

// ---------- Aplicador ----------

function setMetaName(name: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaProperty(prop: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', prop);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function applySEO(config: SEOConfig): void {
  const description = trim(config.description);
  document.title = config.title;
  setMetaName('description', description);
  setMetaProperty('og:title', config.title);
  setMetaProperty('og:description', description);
  setMetaProperty('og:type', config.ogType ?? 'website');
  setMetaProperty('og:site_name', BRAND);
  if (config.ogImage) setMetaProperty('og:image', config.ogImage);
  if (config.canonical) {
    setCanonical(config.canonical);
    setMetaProperty('og:url', config.canonical);
  }
  if (config.keywords?.length) {
    setMetaName('keywords', config.keywords.join(', '));
  }
}

/** Hook React — aplica SEO al montar; recompone si cambia. */
export function useSEO(config: SEOConfig): void {
  useEffect(() => {
    applySEO(config);
  }, [config]);
}
